import {
  AkVertrag, OPT_AK_ART, OPT_AV_ART, OPT_SACH, OPT_SACH_NONE, SachVertrag, VorsorgeVertrag,
} from "../state/schema";
import {
  card, h, jaNein, note, objSelect, objText, textareaInput, unknownToggle,
} from "../components/ui";
import { uploadBlock } from "../components/upload";
import { vAltersvorsorge, vArbeitskraft, vGesundheit, vSach } from "../logic/validation";
import { Step } from "./types";

const newId = () => "v-" + Math.random().toString(36).slice(2, 10);
const persist = (ctx: { store: { persist(): void } }) => () => ctx.store.persist();

/* ---------- 5 · Gesundheit & Risikofaktoren ---------- */
export const stepGesundheit: Step = {
  id: "gesundheit",
  section: "absicherung",
  eyebrow: "Absicherung",
  title: "Gesundheit & Risikofaktoren",
  subtitle: "Nur Ja/Nein – bitte keine Diagnosen oder Einzelheiten.",
  validate: vGesundheit,
  build(ctx) {
    const s = ctx.store;
    const hobbyDetail = h("div");
    const renderHobby = () => {
      hobbyDetail.innerHTML = "";
      if (s.get("gesundheit.risikohobby") === "ja") {
        hobbyDetail.appendChild(h("div", { style: { marginTop: "10px" } },
          textareaInput(s, "gesundheit.risikohobbyText", { label: "Welche Hobbys übst du aus?", required: true, rows: 2, placeholder: "z. B. Motorrad, Klettern, Tauchen …" })));
      }
    };
    renderHobby();
    return h(
      "div",
      {},
      card(
        h("div", { class: "card__title", html: "Körperliche Gesundheit <span style='color:var(--neg)'>*</span>" }),
        h("p", { class: "card__hint", text: "Bestanden oder bestehen in den letzten fünf Jahren körperliche Erkrankungen, Beschwerden, Verletzungen, Operationen oder Behandlungen?" }),
        jaNein(s, "gesundheit.koerperlich")
      ),
      card(
        h("div", { class: "card__title", html: "Psychische Gesundheit <span style='color:var(--neg)'>*</span>" }),
        h("p", { class: "card__hint", text: "Bestanden oder bestehen in den letzten fünf Jahren psychische oder psychosomatische Beschwerden, Erkrankungen, Behandlungen oder Beratungen?" }),
        jaNein(s, "gesundheit.psychisch"),
        note("info", "Bitte nenne hier keine Diagnosen oder Einzelheiten. Falls eine genauere Prüfung erforderlich ist, besprechen wir das später gesondert und vertraulich mit dir.")
      ),
      card(
        h("div", { class: "card__title", html: "Rauchst du? <span style='color:var(--neg)'>*</span>" }),
        jaNein(s, "gesundheit.raucher")
      ),
      card(
        h("div", { class: "card__title", html: "Übst du risikoreiche oder außergewöhnliche Hobbys aus? <span style='color:var(--neg)'>*</span>" }),
        jaNein(s, "gesundheit.risikohobby", renderHobby),
        hobbyDetail
      )
    );
  },
};

/* ---------- 6 · Altersvorsorge ---------- */
export const stepAltersvorsorge: Step = {
  id: "altersvorsorge",
  section: "absicherung",
  eyebrow: "Absicherung",
  title: "Altersvorsorge",
  subtitle: "Hast du bereits für später vorgesorgt?",
  validate: vAltersvorsorge,
  build(ctx) {
    const s = ctx.store;
    const list = h("div");
    const p = persist(ctx);

    const renderCard = (v: VorsorgeVertrag, idx: number): HTMLElement => {
      const body = h("div");
      const draw = () => {
        body.innerHTML = "";
        body.append(
          objSelect(v, "vertragsart", OPT_AV_ART, p, { label: "Vertragsart", required: true }),
          objText(v, "anbieter", p, { label: "Anbieter", required: true, disabled: v.anbieterUnbekannt }),
          unknownToggle(v, "anbieterUnbekannt", p, draw, "Anbieter ist mir nicht bekannt"),
          objText(v, "beitrag", p, { label: "Monatlicher Beitrag", money: true, required: true, disabled: v.beitragUnbekannt }),
          unknownToggle(v, "beitragUnbekannt", p, draw, "Beitrag ist mir nicht bekannt"),
          h("div", { class: "grid-2" },
            objText(v, "produkt", p, { label: "Produktname", optional: true }),
            objText(v, "vertragsnummer", p, { label: "Vertragsnummer", optional: true })),
          h("div", { class: "grid-2" },
            objText(v, "beginn", p, { label: "Vertragsbeginn", optional: true, placeholder: "z. B. 2019" }),
            objText(v, "ende", p, { label: "Vertragsende", optional: true })),
          objText(v, "hochrechnung", p, { label: "Hochrechnung", optional: true }),
          h("div", { class: "field" },
            h("div", { class: "field__label", text: "Versicherungsschein (optional)" }),
            h("p", { class: "field__optional", text: "Der Versicherungsschein ist die Police deines Vertrags. Liegt dir die Police nicht vor, genügen auch die ersten ein bis zwei Seiten des Antrags." }),
            uploadBlock(s, { kategorie: "Altersvorsorge" }))
        );
      };
      draw();
      return h("div", { class: "subcard" },
        h("div", { class: "subcard__head" },
          h("div", { class: "subcard__title", text: `Vertrag ${idx + 1}` }),
          h("button", { class: "btn-remove", type: "button", text: "Entfernen",
            on: { click: () => { s.update((st) => { st.altersvorsorge.vertraege.splice(idx, 1); }, false); renderList(); } } })),
        body);
    };

    const renderList = () => {
      list.innerHTML = "";
      if (s.get("altersvorsorge.vorhanden") !== "ja") return;
      s.state.altersvorsorge.vertraege.forEach((v, i) => list.appendChild(renderCard(v, i)));
      list.appendChild(h("button", { class: "btn-add", type: "button", text: "＋ Weitere Altersvorsorge hinzufügen",
        on: { click: () => { s.update((st) => st.altersvorsorge.vertraege.push(emptyVorsorge()), false); renderList(); } } }));
    };

    const onToggle = () => {
      if (s.get("altersvorsorge.vorhanden") === "ja" && s.state.altersvorsorge.vertraege.length === 0)
        s.update((st) => st.altersvorsorge.vertraege.push(emptyVorsorge()), false);
      renderList();
    };
    renderList();

    return h("div", {},
      card(
        h("div", { class: "card__title", html: "Hast du bereits eine private, betriebliche oder sonstige Altersvorsorge? <span style='color:var(--neg)'>*</span>" }),
        jaNein(s, "altersvorsorge.vorhanden", onToggle, true)),
      list);
  },
};

function emptyVorsorge(): VorsorgeVertrag {
  return { id: newId(), vertragsart: "", anbieter: "", anbieterUnbekannt: false, beitrag: "", beitragUnbekannt: false, produkt: "", beginn: "", ende: "", hochrechnung: "", vertragsnummer: "" };
}

/* ---------- 7 · Arbeitskraftabsicherung ---------- */
export const stepArbeitskraft: Step = {
  id: "arbeitskraft",
  section: "absicherung",
  eyebrow: "Absicherung",
  title: "Arbeitskraftabsicherung",
  subtitle: "Was passiert, wenn du nicht mehr arbeiten kannst?",
  validate: vArbeitskraft,
  build(ctx) {
    const s = ctx.store;
    const list = h("div");
    const p = persist(ctx);

    const renderCard = (v: AkVertrag, idx: number): HTMLElement => {
      const body = h("div");
      const draw = () => {
        body.innerHTML = "";
        body.append(
          objSelect(v, "vertragsart", OPT_AK_ART, p, { label: "Art der Absicherung", required: true }),
          objText(v, "anbieter", p, { label: "Anbieter", required: true, disabled: v.anbieterUnbekannt }),
          unknownToggle(v, "anbieterUnbekannt", p, draw, "Anbieter ist mir nicht bekannt"),
          objText(v, "beitrag", p, { label: "Monatlicher Beitrag", money: true, disabled: v.beitragUnbekannt }),
          unknownToggle(v, "beitragUnbekannt", p, draw, "Beitrag ist mir nicht bekannt"),
          objText(v, "leistung", p, { label: "Versicherte Leistung", placeholder: "z. B. monatliche Rente", disabled: v.leistungUnbekannt }),
          unknownToggle(v, "leistungUnbekannt", p, draw, "Leistung ist mir nicht bekannt"),
          h("div", { class: "grid-2" },
            objText(v, "beginn", p, { label: "Vertragsbeginn", optional: true }),
            objText(v, "ende", p, { label: "Vertragsende", optional: true })),
          objText(v, "vertragsnummer", p, { label: "Vertragsnummer", optional: true }),
          h("div", { class: "field" },
            h("div", { class: "field__label", text: "Versicherungsschein (optional)" }),
            h("p", { class: "field__optional", text: "Der Versicherungsschein ist die Police deines Vertrags. Liegt dir die Police nicht vor, genügen auch die ersten ein bis zwei Seiten des Antrags." }),
            uploadBlock(s, { kategorie: "Arbeitskraftabsicherung" }))
        );
      };
      draw();
      return h("div", { class: "subcard" },
        h("div", { class: "subcard__head" },
          h("div", { class: "subcard__title", text: `Absicherung ${idx + 1}` }),
          h("button", { class: "btn-remove", type: "button", text: "Entfernen",
            on: { click: () => { s.update((st) => { st.arbeitskraft.vertraege.splice(idx, 1); }, false); renderList(); } } })),
        body);
    };

    const renderList = () => {
      list.innerHTML = "";
      if (s.get("arbeitskraft.vorhanden") !== "ja") return;
      s.state.arbeitskraft.vertraege.forEach((v, i) => list.appendChild(renderCard(v, i)));
      list.appendChild(h("button", { class: "btn-add", type: "button", text: "＋ Weitere Absicherung hinzufügen",
        on: { click: () => { s.update((st) => st.arbeitskraft.vertraege.push(emptyAk()), false); renderList(); } } }));
    };
    const onToggle = () => {
      if (s.get("arbeitskraft.vorhanden") === "ja" && s.state.arbeitskraft.vertraege.length === 0)
        s.update((st) => st.arbeitskraft.vertraege.push(emptyAk()), false);
      renderList();
    };
    renderList();

    return h("div", {},
      card(
        h("div", { class: "card__title", html: "Besteht bereits eine Absicherung deiner Arbeitskraft? <span style='color:var(--neg)'>*</span>" }),
        jaNein(s, "arbeitskraft.vorhanden", onToggle, true)),
      list);
  },
};

function emptyAk(): AkVertrag {
  return { id: newId(), vertragsart: "", anbieter: "", anbieterUnbekannt: false, beitrag: "", beitragUnbekannt: false, leistung: "", leistungUnbekannt: false, beginn: "", ende: "", vertragsnummer: "" };
}

/* ---------- 8 · Alltags- & Sachversicherungen ---------- */
export const stepSach: Step = {
  id: "sachversicherungen",
  section: "absicherung",
  eyebrow: "Absicherung",
  title: "Alltags- & Sachversicherungen",
  subtitle: "Welche Versicherungen bestehen aktuell? Mehrfachauswahl.",
  leaveMessage: "Deine Absicherung ist erfasst.",
  validate: vSach,
  build(ctx) {
    const s = ctx.store;
    const p = persist(ctx);
    const cardsWrap = h("div");

    const ensureVertraege = () => {
      const auswahl: string[] = s.get("sachversicherungen.auswahl");
      const echte = auswahl.filter((a) => !OPT_SACH_NONE.includes(a));
      s.update((st) => {
        // entferne Karten ohne Auswahl
        st.sachversicherungen.vertraege = st.sachversicherungen.vertraege.filter((v) => echte.includes(v.art));
        // ergänze fehlende
        for (const art of echte) if (!st.sachversicherungen.vertraege.some((v) => v.art === art))
          st.sachversicherungen.vertraege.push(emptySach(art));
      }, false);
    };

    const toggle = (opt: string) => {
      s.update((st) => {
        const arr = st.sachversicherungen.auswahl;
        const i = arr.indexOf(opt);
        if (i >= 0) arr.splice(i, 1);
        else {
          // "Keine"/"Weiß nicht" schließen echte Auswahl aus und umgekehrt
          if (OPT_SACH_NONE.includes(opt)) st.sachversicherungen.auswahl = [opt];
          else {
            for (const none of OPT_SACH_NONE) {
              const ni = arr.indexOf(none);
              if (ni >= 0) arr.splice(ni, 1);
            }
            arr.push(opt);
          }
        }
      }, false);
      ensureVertraege();
      renderChips();
      renderCards();
    };

    const chips = h("div", { class: "chips" });
    const renderChips = () => {
      chips.innerHTML = "";
      const auswahl: string[] = s.get("sachversicherungen.auswahl");
      [...OPT_SACH, ...OPT_SACH_NONE].forEach((opt) => {
        const active = auswahl.includes(opt);
        chips.appendChild(h("button", { type: "button", class: "chip", text: opt, attrs: { "aria-pressed": String(active) }, on: { click: () => toggle(opt) } }));
      });
    };

    const renderCards = () => {
      cardsWrap.innerHTML = "";
      s.state.sachversicherungen.vertraege.forEach((v) => cardsWrap.appendChild(renderSachCard(v, s, p)));
    };

    renderChips();
    renderCards();

    return h("div", {},
      card(
        h("div", { class: "card__title", html: "Welche Versicherungen bestehen aktuell? <span style='color:var(--neg)'>*</span>" }),
        chips),
      cardsWrap);
  },
};

function emptySach(art: string): SachVertrag {
  return { id: newId(), art, anbieter: "", anbieterUnbekannt: false, beitrag: "", intervall: "monatlich", beginn: "", ende: "", leistung: "", vertragsnummer: "" };
}

function renderSachCard(v: SachVertrag, s: any, p: () => void): HTMLElement {
  const body = h("div");
  const draw = () => {
    body.innerHTML = "";
    body.append(
      objText(v, "anbieter", p, { label: "Anbieter", disabled: v.anbieterUnbekannt }),
      unknownToggle(v, "anbieterUnbekannt", p, draw, "Anbieter ist mir nicht bekannt"),
      h("div", { class: "grid-2" },
        objText(v, "beitrag", p, { label: "Beitrag", money: true }),
        objSelect(v, "intervall" as any, ["monatlich", "jaehrlich"], p, { label: "Zahlungsintervall" })),
      h("div", { class: "grid-2" },
        objText(v, "beginn", p, { label: "Vertragsbeginn", optional: true }),
        objText(v, "ende", p, { label: "Vertragsende / Kündigungsfrist", optional: true })),
      objText(v, "leistung", p, { label: "Leistung", optional: true }),
      objText(v, "vertragsnummer", p, { label: "Vertragsnummer", optional: true }),
      h("div", { class: "field" },
        h("div", { class: "field__label", text: "Versicherungsschein (optional)" }),
        h("p", { class: "field__optional", text: "Der Versicherungsschein ist die Police deines Vertrags. Liegt dir die Police nicht vor, genügen auch die ersten ein bis zwei Seiten des Antrags." }),
        uploadBlock(s, { kategorie: "Sachversicherung" }))
    );
  };
  draw();
  return h("div", { class: "subcard" }, h("div", { class: "subcard__title", style: { marginBottom: "10px" }, text: v.art }), body);
}
